import type { ChangeEvent } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  agreementBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    maxHeight: 420,
    overflowY: 'auto',
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
  },
  subsectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.25),
  },
  list: {
    margin: theme.spacing(0.5, 0, 1.5, 0),
    paddingLeft: theme.spacing(3),
    '& > li': {
      marginBottom: theme.spacing(0.5),
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  checkboxRow: {
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

type Classes = Record<string, string>;

const spokeAgreement = (classes: Classes) => (
  <>
    <Typography variant="h6" gutterBottom>
      Spoke Participation Agreement
    </Typography>

    <Typography variant="body2" paragraph>
      <Link
        href="https://hyperioncloud.sharepoint.com/sites/GroupDataOperations/SitePages/Hubs-&-Spokes-Operational-Model.aspx#spoke-participation-agreement"
        target="_blank"
        rel="noopener noreferrer"
      >
        View full policy reference
      </Link>
    </Typography>

    <Typography variant="subtitle1" className={classes.sectionTitle}>
      What Group Data commits to you
    </Typography>
    <Typography variant="body2">
      By creating a spoke, you can rely on Group to provide:
    </Typography>
    <ul className={classes.list}>
      <li>
        A scoped Databricks workspace(s) with compute, quotas, and auto-expiry
        configured
      </li>
      <li>
        Certified gold data shared into your spoke via Delta Sharing, at no
        additional engineering cost to you
      </li>
      <li>Access to Databricks Marketplace public datasets</li>
      <li>
        Platform guardrails covering security, networking, and identity —
        enforced at the platform layer so you do not have to manage them
        yourself
      </li>
      <li>Onboarding training, documentation, and user guides</li>
      <li>
        Production monitoring and incident triage for shared data flowing into
        your spoke
      </li>
      <li>DPIA referral guidance and advice on handling PII</li>
      <li>
        The Group canonical data model and naming conventions as the foundation
        you may extend as required
      </li>
      <li>
        A Unity Catalog metastore with Group-level governance controls in place
      </li>
      <li>
        Technical Design Authority (TDA) review and approval for architectural
        patterns
      </li>
      <li>
        The Group DQ KPI framework so your quality reporting is consistent with
        Group standards (for certified data products)
      </li>
    </ul>

    <Typography variant="subtitle1" className={classes.sectionTitle}>
      What you commit to
    </Typography>
    <Typography variant="body2" paragraph>
      By joining as a spoke, your team agrees to:
    </Typography>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Operate within the spoke boundary
    </Typography>
    <ul className={classes.list}>
      <li>
        Use the spoke for local exploration and analysis. Do not attempt to
        publish data as Group-certified — that responsibility sits with the
        Group Hub only.
      </li>
      <li>
        Work built within your spoke remains your responsibility to own and
        support. Some spokes may operate experimental workloads, while others
        may operate production-grade pipelines and data products.
      </li>
      <li>
        Solutions developed by your own engineers within your spoke are not
        covered by Group engineering support.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Extend the model — never fork it
    </Typography>
    <ul className={classes.list}>
      <li>
        You may add your own data sources and domain-specific data products.
      </li>
      <li>
        You may not modify the Group canonical data model or create a parallel
        version of it.
      </li>
      <li>
        Any extensions to the Group model must follow Group naming conventions.
      </li>
      <li>
        Changes that impact shared structures, shared data products, or
        introduce new architectural patterns require TDA review before
        production implementation.
      </li>
      <li>
        You may not publish data as Group-certified. Only the Group Hub
        certifies data at Group level.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Manage your data responsibly
    </Typography>
    <ul className={classes.list}>
      <li>
        Before loading any data that contains PII or is subject to regulatory
        classification, flag it to your divisional data officer (if you have
        one), or the Group Data Governance team.
      </li>
      <li>Do not load regulated data into a spoke without prior guidance.</li>
      <li>
        Do not attempt to circumvent platform guardrails or access data outside
        your granted permissions.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Own your local work
    </Typography>
    <ul className={classes.list}>
      <li>
        Any pipelines, notebooks, or processes you build locally in the spoke
        are your responsibility to maintain and support.
      </li>
      <li>Group will not triage failures in locally built spoke assets.</li>
      <li>
        If you encounter an issue with shared data (gold data flowing in from a
        hub), raise it through the proper support channel — do not attempt to
        alter the shared data directly.
      </li>
      <li>
        Group provides platform services and governance frameworks, but
        operational responsibility for spoke-owned pipelines, data products,
        notebooks, dashboards, and business logic remains with the spoke.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Keep your spoke current
    </Typography>
    <ul className={classes.list}>
      <li>
        Maintain a named team lead for your spoke at all times. If your team
        lead changes, notify the Platform Team.
      </li>
      <li>
        Review your spoke's compute usage and budget cap quarterly. Notify the
        Platform Team if your needs change significantly.
      </li>
      <li>
        Decommission your spoke through the proper process when it is no longer
        needed. Do not leave unused spoke environments running.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Manage your costs
    </Typography>
    <ul className={classes.list}>
      <li>
        You are accountable for the compute and storage costs generated by your
        spoke. Review your FinOps data regularly.
      </li>
      <li>
        Do not provision resources beyond your agreed budget without Platform
        Team approval.
      </li>
    </ul>

    <Typography variant="subtitle1" className={classes.sectionTitle}>
      What you are not signing up for
    </Typography>
    <Typography variant="body2" paragraph>
      Joining as a spoke does not mean you are immediately taking on data
      engineering responsibility, regulatory accountability, or formal
      governance obligations.
    </Typography>
    <Typography variant="body2" paragraph>
      That can come later as your usage evolves and your engineering capability
      matures.
    </Typography>
    <Typography variant="body2" paragraph>
      Most spokes will consume data, perform local analysis, and create reports
      or insights for their own teams. Those activities do not require formal
      data product ownership, certification, or governance structures beyond the
      standard platform controls.
    </Typography>
    <Typography variant="body2" paragraph>
      If your needs evolve and you choose to publish data products for use by
      other teams, additional responsibilities apply. We can guide you through
      this process, along with the product certification process. Please contact
      <Link href="mailto:datasphere@howdengrp.com">
        {' '}
        datasphere@howdengrp.com
      </Link>
      .
    </Typography>

    <Typography variant="subtitle1" className={classes.sectionTitle}>
      If your spoke publishes (certified) data products
    </Typography>
    <Typography variant="body2" paragraph>
      Some spokes will mature beyond local analysis and begin publishing data
      products for consumption by other teams. Where this happens, additional
      responsibilities apply to ensure consumers can trust, understand, and
      safely use published data.
    </Typography>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Publish responsibly
    </Typography>
    <ul className={classes.list}>
      <li>
        If assets built in your spoke prove their value and you want to share
        them more broadly, follow the promotion and certification process.
      </li>
      <li>
        Do not distribute spoke-local data outside your team without following
        the appropriate governance and approval processes.
      </li>
      <li>Published data products must have an identified owner.</li>
      <li>
        Published data products must include documentation describing their
        purpose, contents, lineage, and intended usage.
      </li>
      <li>
        Breaking changes to published data products must be communicated to
        consumers in advance wherever reasonably possible.
      </li>
      <li>
        These rules are in place to ensure that Howden meets its local data
        sovereignty and GDPR (or similar) commitments.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Maintain data quality
    </Typography>
    <ul className={classes.list}>
      <li>
        Pipelines supporting published or certified data products must apply
        data quality controls at ingestion and throughout the transformation
        process.
      </li>
      <li>
        Published data products should be aligned to the Group DQ KPI framework
        where applicable.
      </li>
      <li>
        Quality failures in published data products are the responsibility of
        the publishing spoke to detect, triage, and resolve (or respond to bug
        reports according to their published SLA).
      </li>
      <li>
        Consumers of published data products are entitled to clearly defined
        support routes and, where appropriate, service-level commitments.
      </li>
      <li>
        Quality metrics for certified data products must be reported to Group on
        the agreed cadence.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Maintain governance and accountability
    </Typography>
    <ul className={classes.list}>
      <li>
        Spokes that publish certified or widely consumed data products must
        appoint a Divisional Data Officer (DDO), or equivalent accountable
        business owner, responsible for governance, compliance, and data quality
        oversight.
      </li>
      <li>
        The DDO is accountable for ensuring that published data products meet
        required governance, security, privacy, and compliance standards.
      </li>
      <li>
        Published data products must remain aligned to the Group canonical data
        model and naming conventions.
      </li>
      <li>
        Any changes affecting shared structures, shared data products, or
        introducing new architectural patterns must be reviewed through the
        appropriate governance processes and may require TDA approval before
        production implementation.
      </li>
    </ul>

    <Typography variant="body2" className={classes.subsectionTitle}>
      Own compliance obligations
    </Typography>
    <ul className={classes.list}>
      <li>
        For spokes publishing data products, regional regulatory compliance —
        including GDPR, local data residency requirements, and any
        insurance-sector obligations specific to your jurisdiction — remains the
        responsibility of the publishing spoke and its appointed DDO.
      </li>
      <li>
        Group provides the framework and the platform controls. It does not
        provide sign-off on your regulatory position.
      </li>
      <li>
        PII classification, DPIA oversight, retention policies, and data
        sovereignty obligations for published data products are the
        responsibility of the publishing spoke, supported by guidance from the
        Group DPO where required.
      </li>
    </ul>

    <Typography variant="body2">
      <Link
        href="https://hyperioncloud.sharepoint.com/sites/GroupDataOperations/SitePages/Hubs-&-Spokes-Operational-Model.aspx"
        target="_blank"
        rel="noopener noreferrer"
      >
        Hubs and Spokes Operational Model
      </Link>
    </Typography>
  </>
);

export const ParticipationAgreementField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<boolean>) => {
  const classes = useStyles();
  const checked = formData === true;
  const hasError = Boolean(rawErrors?.length);

  const handleChange = (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
    onChange(value);
  };

  return (
    <FormControl
      required={required}
      error={hasError}
      component="fieldset"
      fullWidth
      className={classes.root}
    >
      <div className={classes.agreementBox}>{spokeAgreement(classes)}</div>

      <FormControlLabel
        className={classes.checkboxRow}
        control={
          <Checkbox checked={checked} onChange={handleChange} color="primary" />
        }
        label={
          <Typography variant="body2">
            I have read and agree to the participation agreement above
            {required ? ' *' : ''}
          </Typography>
        }
      />

      {hasError && (
        <FormHelperText className={classes.errorText}>
          You must agree to the participation agreement before continuing.
        </FormHelperText>
      )}
    </FormControl>
  );
};
